import { insightAuthoringApi } from '../api/InsightAuthoringApi'
import type {
  AuthoringTurn,
  InsightDraftRequest,
  InsightDraftResponse,
} from '../models/insightAuthoring.types'
import { MAX_AUTHORING_TURNS } from '../models/insightAuthoring.types'
import {
  describeAuthoringError,
  type AuthoringErrorDescriptor,
} from '../service/insightAuthoringErrors'
import {
  createInsightAuthoringTelemetry,
  type InsightAuthoringTelemetry,
} from '../service/insightAuthoringTelemetry'

import { ApiError } from '@/shared/http'

interface AuthoringTurnApiPort {
  draft: (input: InsightDraftRequest, signal?: AbortSignal) => Promise<InsightDraftResponse>
}

export interface AuthoringTurnCommand {
  sessionId: string | null
  userMessage: string
  previousDraft: InsightDraftRequest['previousDraft']
  turnCount: number
  signal?: AbortSignal
}

export type AuthoringTurnOutcome =
  | {
      kind: 'received'
      sessionId: string
      turn: AuthoringTurn
      draft: InsightDraftResponse['draft']
      diff: InsightDraftResponse['diff']
    }
  | { kind: 'invalid' | 'capped'; error: AuthoringErrorDescriptor }
  | { kind: 'canceled' }
  | { kind: 'error'; error: AuthoringErrorDescriptor }

export class AuthoringTurnFlow {
  constructor(
    private readonly api: AuthoringTurnApiPort = insightAuthoringApi,
    private readonly telemetry: InsightAuthoringTelemetry = createInsightAuthoringTelemetry(),
  ) {}

  async execute(command: AuthoringTurnCommand): Promise<AuthoringTurnOutcome> {
    const userMessage = command.userMessage.trim()
    if (!userMessage) {
      return { kind: 'invalid', error: describeAuthoringError({ code: 'invalid_input' }) }
    }
    if (command.turnCount >= MAX_AUTHORING_TURNS) {
      return { kind: 'capped', error: describeAuthoringError({ code: 'session_too_long' }) }
    }

    if (!command.sessionId && command.turnCount === 0) this.telemetry.trackSessionStarted()
    this.telemetry.trackTurnSent({
      ...(command.sessionId ? { sessionId: command.sessionId } : {}),
      messageLen: userMessage.length,
    })
    try {
      const response = await this.api.draft(
        {
          sessionId: command.sessionId,
          userMessage,
          previousDraft: command.previousDraft ?? null,
        },
        command.signal,
      )
      const turn: AuthoringTurn = {
        turn_id: response.turn_id,
        user_message: userMessage,
        notes: response.notes,
        warnings: response.warnings,
      }
      this.telemetry.trackTurnReceived({
        sessionId: response.session_id,
        turnId: response.turn_id,
        warningsCount: response.warnings.length,
      })
      return {
        kind: 'received',
        sessionId: response.session_id,
        turn,
        draft: response.draft,
        diff: response.diff,
      }
    } catch (cause) {
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (error.kind === 'canceled') return { kind: 'canceled' }
      return { kind: 'error', error: describeAuthoringError(error) }
    }
  }
}
